import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import Drawer from "antd/es/drawer";
import Form from "antd/es/form";
import { useForm, useWatch } from "antd/es/form/Form";
import { useIntl } from "react-intl";

import CustomFieldDataType from "@mapmycustomers/shared/enum/CustomFieldDataType";
import NumberFormatType from "@mapmycustomers/shared/enum/NumberFormatType";
import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import { EntityTypesSupportingFieldCustomization } from "@mapmycustomers/shared/types/entity";
import Role from "@mapmycustomers/shared/types/Role";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import Team from "@mapmycustomers/shared/types/Team";
import { Button, LargeTextField, TextField } from "@mapmycustomers/ui";

import CurrencySelect from "@app/component/CurrencySelect";
import Checkbox from "@app/component/input/Checkbox";
import FieldAccess from "@app/scene/settings/component/CustomFields/component/CreateEditPane/component/FieldAccess";
import NumberFormat from "@app/scene/settings/component/CustomFields/component/CreateEditPane/component/NumberFormatType";
import DefaultCustomFieldFormValues from "@app/scene/settings/component/CustomFields/type/DefaultCustomFieldFormValues";
import isDataTypeOptionable from "@app/scene/settings/component/CustomFields/util/isDataTypeOptionable";
import Options from "@app/scene/settings/component/ManageFields/component/Options";
import {
  cancelEditField,
  updateField,
  validateField,
} from "@app/scene/settings/component/ManageFields/store/actions";
import {
  getEditedCustomField,
  getEditedField,
  isEditedFieldLoading,
} from "@app/scene/settings/component/ManageFields/store/selectors";
import canBeUnique from "@app/scene/settings/util/canBeUnique";
import { getRolesExcludingOwners, getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService, useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import {
  createTeamVisibilityFromRoleVisibility,
  doTeamsHaveUniformRoles,
} from "@app/util/schema/fieldVisibilityRecord";
import getDefaultDrawerContainer from "@app/util/ui/getPageContentContainer";
import useHiddenChat from "@app/util/useHiddenChat";

import FieldRequiredAlert from "../FieldRequiredAlert";
import showFieldUniqueConfirmModal from "../FieldUniqueConfirmModal";

import FieldDetails from "./component/FieldDetails";
import styles from "./EditPane.module.scss";
import messages from "./messages";

interface Props {
  analyticIssuer?: AnalyticsService;
  cancelEditField: typeof cancelEditField;
  customField?: CustomField;
  loading: boolean;
  onValidateField: typeof validateField.request;
  roles: Role[];
  schemaField: SchemaField;
  teams: Team[];
  updateField: typeof updateField.request;
}

const EditPane: React.FC<Props> = ({
  analyticIssuer,
  cancelEditField,
  customField,
  loading,
  onValidateField,
  roles,
  schemaField,
  teams,
  updateField,
}) => {
  const intl = useIntl();
  const [form] = useForm<DefaultCustomFieldFormValues>();

  const isUnique = useWatch("isUnique", form);
  const displayType = useWatch("displayType", form);
  const editFieldAnalytics = useExtendedAnalytics(["Field", "Edit Field"], analyticIssuer);

  const entityType = schemaField.entity as EntityTypesSupportingFieldCustomization;
  const field = getFieldModelByEntityType(entityType).getByPlatformName(schemaField.field);

  const customFieldDataType = customField?.dataType;

  const hasPlaceholder =
    customFieldDataType &&
    [CustomFieldDataType.LARGE_TEXT, CustomFieldDataType.TEXT].includes(customFieldDataType);
  const hasCurrency = customFieldDataType === CustomFieldDataType.MONETARY;
  const hasOptions = customFieldDataType && isDataTypeOptionable(customFieldDataType);
  const hasFormat = customFieldDataType === CustomFieldDataType.DECIMAL;

  useEffect(() => {
    editFieldAnalytics?.clicked([]);
  }, [editFieldAnalytics]);

  const handleSave = useCallback(() => {
    editFieldAnalytics?.clicked(["Save"]);
    form.submit();
  }, [editFieldAnalytics, form]);

  const handleClose = useCallback(() => {
    cancelEditField();
  }, [cancelEditField]);

  const handleUpdateField = useCallback(() => {
    const formValues = form.getFieldsValue();

    const teamVisibility = formValues.unifiedVisibility
      ? createTeamVisibilityFromRoleVisibility(formValues.roleVisibility, roles, teams)
      : formValues.teamVisibility;

    updateField({
      customField: customField
        ? {
            displayName: formValues.displayName,
            isUnique: formValues.isUnique,
            ...(customField?.isCalculated
              ? { expressionDesc: formValues.expressionDesc ?? "" }
              : {}),
            ...(hasOptions ? { options: formValues.options ?? [] } : {}),
            ...(hasPlaceholder ? { placeholder: formValues.placeholder ?? "" } : {}),
            ...(hasCurrency ? { currencyId: formValues.currencyId ?? "" } : {}),
            ...(hasFormat
              ? { displayType: formValues.displayType ?? NumberFormatType.NUMERIC }
              : {}),
          }
        : undefined,
      field: {
        ...schemaField,
        phiEnabled: formValues.phiEnabled,
        teamVisibility,
        visibility: formValues.visibility,
      },
    });
  }, [
    customField,
    form,
    hasCurrency,
    hasFormat,
    hasOptions,
    hasPlaceholder,
    roles,
    schemaField,
    teams,
    updateField,
  ]);

  const handleSubmit = useCallback(() => {
    const isUnique: boolean = form.getFieldValue("isUnique");

    if (customField && isUnique) {
      onValidateField({
        id: customField?.id,
        failureCallback: () => {
          showFieldUniqueConfirmModal(intl);
        },
        successCallback: handleUpdateField,
      });
    } else {
      handleUpdateField();
    }
  }, [form, customField, onValidateField, handleUpdateField, intl]);

  const teamVisibility = useMemo(() => {
    const teamIds = new Set(teams.map(({ id }) => id));
    // filter out visibilities for teams which no longer exist
    return (schemaField.teamVisibility ?? []).filter(
      ({ team }) => team === null || teamIds.has(team.id)
    );
  }, [schemaField.teamVisibility, teams]);

  const isUnifiedVisibility = useMemo(
    () => doTeamsHaveUniformRoles(teamVisibility, roles),
    [teamVisibility, roles]
  );

  const initialValues: Partial<DefaultCustomFieldFormValues> = useMemo(() => {
    const roleVisibility = isUnifiedVisibility
      ? roles.reduce((result, { id }) => {
          if (result[id]) {
            return result;
          }
          const visibility = teamVisibility.find(({ role }) => role.id === id)?.visibility;
          return visibility ? Object.assign(result, { [id]: visibility }) : result;
        }, {} as DefaultCustomFieldFormValues["roleVisibility"])
      : ({} as DefaultCustomFieldFormValues["roleVisibility"]);

    return {
      currencyId: customField?.currencyId,
      dataType: customField?.dataType,
      displayName:
        customField?.displayName ?? field?.fieldManagementDisplayName ?? schemaField.field,
      displayType: customField?.displayType,
      expressionDesc: customField?.expressionDesc ?? "",
      isUnique: customField?.isUnique ?? false,
      options: customField?.options,
      phiEnabled: schemaField.phiEnabled,
      placeholder: customField?.placeholder ?? "",
      roleVisibility,
      teamVisibility: isUnifiedVisibility ? [] : teamVisibility,
      unifiedVisibility: isUnifiedVisibility,
      visibility: schemaField.visibility,
    };
  }, [customField, schemaField, field, isUnifiedVisibility, roles, teamVisibility]);

  useHiddenChat();

  return (
    <Drawer
      className={styles.container}
      closable
      getContainer={getDefaultDrawerContainer()}
      onClose={handleClose}
      open
      placement="right"
      title={intl.formatMessage(messages.editPaneTitle)}
      zIndex={1}
    >
      <div className={styles.content}>
        <FieldDetails customField={customField} field={schemaField} />

        <hr />

        <Form<DefaultCustomFieldFormValues>
          form={form}
          initialValues={initialValues}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="displayName"
            rules={[
              {
                message: intl.formatMessage(messages.error),
                required: true,
              },
            ]}
          >
            <TextField
              className={styles.nameField}
              label={intl.formatMessage(messages.nameTitle)}
              locked={!customField}
              size="large"
            />
          </Form.Item>
          {customField?.isCalculated && (
            <Form.Item name="expressionDesc">
              <LargeTextField label={intl.formatMessage(messages.expressionDescription)} />
            </Form.Item>
          )}

          {hasPlaceholder && (
            <Form.Item name="placeholder">
              <TextField
                label={intl.formatMessage(messages.placeholderTitle)}
                placeholder={intl.formatMessage(messages.placeholder)}
                size="large"
              />
            </Form.Item>
          )}

          {hasCurrency && (
            <Form.Item
              name="currencyId"
              rules={[
                {
                  message: intl.formatMessage(messages.currencyError),
                  required: true,
                },
              ]}
            >
              <CurrencySelect
                disabled
                label={intl.formatMessage(messages.currencyTitle)}
                size="large"
                useCurrencyId
              />
            </Form.Item>
          )}
          {hasFormat && (
            <Form.Item name="displayType">
              <NumberFormat label={intl.formatMessage(messages.format)} />
            </Form.Item>
          )}

          {customFieldDataType && canBeUnique(customFieldDataType, displayType) && (
            <Form.Item name="isUnique" valuePropName="checked">
              <Checkbox>{intl.formatMessage(messages.markUnique)}</Checkbox>
            </Form.Item>
          )}
          {hasOptions && <Options calculatedField={customField?.isCalculated} form={form} />}

          <FieldAccess calculatedField={customField?.isCalculated} schemaField={schemaField} />
        </Form>
        <FieldRequiredAlert isUnique={isUnique} />
      </div>

      <div className={styles.footer}>
        <Button loading={loading} onClick={handleSave} type="primary">
          {intl.formatMessage(messages.saveButton)}
        </Button>
      </div>
    </Drawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  customField: getEditedCustomField(state),
  loading: isEditedFieldLoading(state),
  roles: getRolesExcludingOwners(state),
  schemaField: getEditedField(state)!,
  teams: getTeams(state),
});

const mapDispatchToProps = {
  cancelEditField,
  onValidateField: validateField.request,
  updateField: updateField.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditPane);
